import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Certificate as CertificateIcon, Printer } from "@phosphor-icons/react";

// HTML-escape user-provided strings before injecting into a document.write payload.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export default function CertificateView() {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/certificates/${certificateId}`, { withCredentials: true });
      setCert(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load certificate");
      navigate("/training");
    } finally {
      setLoading(false);
    }
  }, [certificateId, navigate]);

  useEffect(() => { load(); }, [load]);

  const print = () => {
    if (!cert) return;
    const LOGO_URL = "https://customer-assets.emergentagent.com/job_role-flow-hub/artifacts/rlqwkxc4_logo.png";
    const name = cert.employee?.name || "";
    const html = `
<!doctype html><html><head><title>Certificate — ${esc(cert.training_title)}</title>
<style>
  body{font-family:Georgia,serif;margin:0;padding:60px;background:#f8f5e8}
  .cert{max-width:900px;margin:0 auto;background:#fff;padding:60px;border:8px double #1c4587;text-align:center}
  .logo{height:90px;margin:0 auto 24px;display:block;object-fit:contain}
  h1{font-size:48px;color:#1c4587;margin:0 0 10px}
  .sub{color:#555;font-size:18px;margin-bottom:40px}
  .name{font-size:44px;font-style:italic;color:#000;margin:20px 0;border-bottom:2px solid #1c4587;display:inline-block;padding:0 40px 10px}
  .body{font-size:18px;line-height:1.6;color:#333;margin:30px 0}
  .meta{display:flex;justify-content:space-around;margin-top:60px;color:#555;font-size:13px}
  .meta div{text-align:center}
  .label{font-size:11px;text-transform:uppercase;color:#999;letter-spacing:1px}
</style></head><body>
  <div class="cert">
    <img class="logo" src="${LOGO_URL}" alt="CME" crossorigin="anonymous" />
    <h1>Certificate of Completion</h1>
    <p class="sub">This is to certify that</p>
    <div class="name">${esc(name)}</div>
    <p class="body">has successfully completed the training programme<br><strong>${esc(cert.training_title)}</strong><br>with a score of <strong>${esc(cert.score)}%</strong></p>
    <div class="meta">
      <div><div class="label">Issued</div>${esc(cert.issued_date.split("T")[0])}</div>
      <div><div class="label">Valid Until</div>${esc(cert.expiry_date.split("T")[0])}</div>
      <div><div class="label">Certificate ID</div>${esc(cert.certificate_id)}</div>
    </div>
  </div>
  <script>
    window.addEventListener('load', function(){
      var img = document.querySelector('.logo');
      if (img.complete) { setTimeout(()=>window.print(), 300); }
      else { img.addEventListener('load', ()=>setTimeout(()=>window.print(), 300)); img.addEventListener('error', ()=>setTimeout(()=>window.print(), 300)); }
    });
  </script>
</body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  if (loading || !cert) {
    return <div className="min-h-screen flex items-center justify-center bg-secondary"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="certificate-view">
      <header className="glass-header border-b border-black/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <Button variant="ghost" size="sm" onClick={() => navigate("/training")}><ArrowLeft size={18} className="mr-2" />Back to Trainings</Button>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="pt-6 text-center">
            <CertificateIcon size={40} weight="bold" className="text-primary mx-auto mb-2" />
            <h2 className="text-2xl font-bold mb-1">{cert.training_title}</h2>
            <p className="text-muted-foreground mb-4">Certificate of Completion</p>
            <div className="grid grid-cols-3 gap-3 text-sm text-left mb-6">
              <div><span className="text-muted-foreground">Awarded to</span><br /><strong>{cert.employee?.name}</strong></div>
              <div><span className="text-muted-foreground">Score</span><br /><strong>{cert.score}%</strong></div>
              <div><span className="text-muted-foreground">Issued</span><br /><strong>{cert.issued_date.split("T")[0]}</strong></div>
              <div><span className="text-muted-foreground">Valid until</span><br /><strong>{cert.expiry_date.split("T")[0]}</strong></div>
              <div className="col-span-2"><span className="text-muted-foreground">Certificate ID</span><br /><code className="text-xs bg-secondary px-2 py-1 rounded">{cert.certificate_id}</code></div>
            </div>
            <div className="flex justify-center gap-2">
              <Button onClick={print} data-testid="print-certificate-btn"><Printer size={16} className="mr-2" />Download / Print</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
